import React, { useContext, useState } from 'react';
import {
  Form,
  Input,
  Typography,
  Space,
  Divider,
  notification,
  Flex,
  Tooltip,
  Button,
  Card,
} from 'antd';
import { LockOutlined, UserOutlined, MoonOutlined } from '@ant-design/icons';
import FormItem from 'antd/es/form/FormItem';
import { FaLock } from 'react-icons/fa';
import {
  useLoginMutation,
  useMfaVerifyMutation,
  useVerifyTrustedDeviceMutation,
} from '@/state/slices/auth.ts';
import {
  useLazyGetSettingsQuery,
  usePatchSettingMutation,
} from '@/state/slices/setting';
import { useNavigate } from 'react-router-dom';
import {
  UserContext,
  ThemeContext,
  SettingsContext,
} from '@/state/contexts.ts';
import type { ILoginResponse, IUser } from '@/models/auth.ts';
import { MFAVerify } from '@/features/mfa';
import { getDeviceId } from '@/shared/utils/deviceId';
import {
  getTrustedDeviceUserKey,
  setDeviceTrustedLocally,
} from '@/shared/utils/trustedDeviceStore';
import { getFetchErrorDetail } from '@/shared/utils/rtkError';
import iCardioLogoLight from '@/images/icardio-logo-light.svg';
import iCardioLogoBlack from '@/images/icardio-logo-dark.svg';
import styles from './Login.module.css';

const MFA_TRUSTED_DEVICE_HINT =
  'Tip: Registering from Settings (toggle Remember this device off and on) saves this browser as trusted.';

const { Text } = Typography;

interface FieldType {
  email?: string;
  password?: string;
  remember?: string;
}

const Login: React.FC = () => {
  const [api, contextHolder] = notification.useNotification();
  const [loginFn] = useLoginMutation();
  const [mfaVerifyFn, { isLoading: isVerifying }] = useMfaVerifyMutation();
  const [verifyTrustedDeviceFn] = useVerifyTrustedDeviceMutation();
  const [fetchSettings] = useLazyGetSettingsQuery();
  const [patchSetting] = usePatchSettingMutation();
  const { setUser } = useContext(UserContext);
  const { darkTheme, setDarkTheme } = useContext(ThemeContext);
  const { setSettings } = useContext(SettingsContext);
  const navigate = useNavigate();

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | undefined>();
  const [mfaHint, setMfaHint] = useState<string | undefined>();
  const [rememberDevice, setRememberDevice] = useState(false);
  const [pendingLoginEmail, setPendingLoginEmail] = useState('');

  const completeLogin = (
    response: ILoginResponse | IUser,
    opts?: { syncRememberDeviceSetting?: boolean }
  ) => {
    if (!response.token) return;

    const user: IUser = {
      id: response.id || '',
      email: response.email || '',
      firstName: response.firstName || '',
      lastName: response.lastName || '',
      role: response.role || '',
      token: response.token,
      mfaEnabled: response.mfaEnabled,
    };
    localStorage.setItem('AccessToken', user.token);
    localStorage.setItem('isAuthenticated', JSON.stringify(true));
    localStorage.setItem('sessionStart', Date.now().toString());
    setUser(user);
    localStorage.setItem('user', JSON.stringify(user));

    if (opts?.syncRememberDeviceSetting) {
      setSettings({ rememberDeviceEnabled: true });
      // Trusted device is already stored by MFA verify; PATCH settings in background
      // so navigation is never blocked by GET/PATCH latency.
      (async () => {
        try {
          const page = await fetchSettings().unwrap();
          const uuid = page.results?.[0]?.uuid;
          if (uuid) {
            await patchSetting({
              uuid,
              data: { rememberDeviceEnabled: true },
            }).unwrap();
          }
        } catch {
          /* best effort */
        }
      })();
    }

    navigate(user.mfaEnabled ? '/' : '/mfa-setup');
  };

  const onFinish = async (values: FieldType) => {
    setIsLoggingIn(true);
    setMfaHint(undefined);
    const submittedEmail = (values.email ?? '').trim().toLowerCase();
    setPendingLoginEmail(submittedEmail);
    try {
      const deviceId = await getDeviceId();
      const result = await loginFn({
        username: submittedEmail,
        password: values.password as string,
        deviceId,
      }).unwrap();

      if (result.mfaRequired && result.mfaToken) {
        try {
          const user = await verifyTrustedDeviceFn({
            mfaToken: result.mfaToken,
            deviceId,
          }).unwrap();
          const trustedKey = getTrustedDeviceUserKey(
            user.id,
            user.email,
            submittedEmail
          );
          setDeviceTrustedLocally(trustedKey, true);
          completeLogin(user);
        } catch (err) {
          const detail = getFetchErrorDetail(err);
          setMfaHint(
            [detail, MFA_TRUSTED_DEVICE_HINT].filter(Boolean).join(' ')
          );
          setMfaToken(result.mfaToken);
        }
        return;
      }

      completeLogin(result);
    } catch {
      api.error({ message: 'Incorrect email or password' });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const onMfaVerify = async (code: string) => {
    if (!mfaToken) return;
    setMfaError(undefined);

    try {
      const deviceId = rememberDevice ? await getDeviceId() : undefined;
      const user = await mfaVerifyFn({
        mfaToken,
        code,
        rememberDevice,
        deviceId,
      }).unwrap();
      if (rememberDevice) {
        const trustedKey = getTrustedDeviceUserKey(
          user.id,
          user.email,
          pendingLoginEmail
        );
        setDeviceTrustedLocally(trustedKey, true);
      }
      completeLogin(user, {
        syncRememberDeviceSetting: rememberDevice,
      });
    } catch {
      setMfaError('Invalid verification code. Please try again.');
    }
  };

  const renderContent = () => {
    if (mfaToken) {
      return (
        <MFAVerify
          onVerify={onMfaVerify}
          loading={isVerifying}
          error={mfaError}
          instructionHint={mfaHint}
          // Always show: account "Remember this device" in Settings only controls
          // whether trusted devices skip MFA; users must still be able to opt in
          // here after turning that setting off.
          showRememberDevice
          rememberDevice={rememberDevice}
          onRememberDeviceChange={setRememberDevice}
          onBack={() => {
            setMfaToken(null);
            setMfaError(undefined);
            setMfaHint(undefined);
            setRememberDevice(false);
          }}
        />
      );
    }

    return (
      <>
        <Space direction="vertical" style={{ width: '100%' }} size={24}>
          <Form
            autoComplete="off"
            layout="vertical"
            requiredMark={false}
            onFinish={onFinish}
          >
            <FormItem<FieldType>
              label={<Text style={{ fontWeight: 600 }}>Email address</Text>}
              name="email"
            >
              <Input
                prefix={<UserOutlined style={{ color: '#6b7280' }} />}
                placeholder="user@example.com"
              />
            </FormItem>

            <FormItem<FieldType>
              label={<Text style={{ fontWeight: 600 }}>Password</Text>}
              name="password"
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#6b7280' }} />}
                placeholder="••••••••"
              />
            </FormItem>

            <Form.Item>
              <Button
                className={styles.submitButton}
                type="primary"
                htmlType="submit"
                loading={isLoggingIn}
              >
                Sign in
              </Button>
            </Form.Item>
          </Form>
        </Space>

        <Divider size="small" />

        <div className={styles.footerNote}>
          <FaLock style={{ color: '#9ca3af' }} /> HIPAA-compliant environment.
          Authorized users only.
        </div>
      </>
    );
  };

  return (
    <div className={styles.pageContainer}>
      {contextHolder}

      <Flex vertical wrap gap="middle">
        <Flex justify="end">
          <Tooltip title="Change theme config" placement="top">
            <Button
              icon={<MoonOutlined />}
              onClick={() => setDarkTheme(!darkTheme)}
            />
          </Tooltip>
        </Flex>

        <Card className={styles.loginCard}>
          <Space orientation="vertical" style={{ width: '100%' }} size={24}>
            <img
              className={styles.logo}
              src={darkTheme ? iCardioLogoLight : iCardioLogoBlack}
              alt="iCardio.ai"
            />
          </Space>
          <Divider size="large" />
          {renderContent()}
        </Card>
      </Flex>
    </div>
  );
};

export default Login;
